import { AddCircleOutline, Close, CloudUpload } from '@material-ui/icons';
import { MenuItem, Select } from '@mui/material';
import _ from 'lodash';
import { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { SchemaDescription } from 'yup';

import styles from '../ProjectSubmissionForm.module.css';
import type { ProjectSubmissionFormInterface } from './schema';
import { schema } from './schema';

const MAX_FILE_SIZE = 11 * 1024 * 1024; // 11MB

interface InputProps {
  label?: string;
  name:
    | keyof ProjectSubmissionFormInterface
    | `team.${number}.name`
    | `team.${number}.image`;
}

const SubmissionInputWrapper: React.FC<
  InputProps & {
    errorMessage?: string;
  }
> = ({ children, label, name, errorMessage }) => {
  const isOptional = (schema.describe().fields[name] as SchemaDescription)
    ?.optional;
  const isRequired = isOptional != null && !isOptional;

  return (
    <div className="w-full inline-flex flex-col gap-2">
      <label>
        {label ?? _.startCase(name)}
        {isRequired ? ' *' : ''}
      </label>
      {children}
      <p className="text-sm text-red-800">{errorMessage ?? '\u00A0'}</p>
    </div>
  );
};

export const SubmissionFormInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> &
    InputProps & {
      isTextarea?: boolean;
    }
> = ({ label, name, isTextarea }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<ProjectSubmissionFormInterface>();

  return (
    <SubmissionInputWrapper
      name={name}
      label={label}
      errorMessage={
        errors[name as keyof ProjectSubmissionFormInterface]?.message
      }
    >
      {isTextarea === true ? (
        <textarea
          {...register(name)}
          className={styles.submissionFormInput + ' pt-3 h-32 '}
          maxLength={2000}
        />
      ) : (
        <input
          {...register(name)}
          className={styles.submissionFormInput + ' h-12'}
          maxLength={150}
        />
      )}
    </SubmissionInputWrapper>
  );
};

interface SubmissionFormSelectProps extends InputProps {
  options: { value: string; label: string }[];
}

const SubmissionFormCommonSelect: React.FC<
  SubmissionFormSelectProps & { isMulti: boolean }
> = ({ name, label, options, isMulti, ...rest }) => {
  const { control } = useFormContext();

  return (
    <SubmissionInputWrapper name={name} label={label}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            {...rest}
            multiple={isMulti}
            renderValue={selected =>
              isMulti
                ? (selected as string[])
                    .map(
                      value =>
                        options.find(option => option.value === value)?.label,
                    )
                    .join(', ')
                : options.find(option => option.value === selected)?.label
            }
            onChange={event => {
              field.onChange(event.target.value);
            }}
            value={field.value || (isMulti ? [] : '')}
            sx={{
              '& .MuiSelect-select': {
                backgroundColor: 'white',
                borderRadius: '5px',
                padding: '10px',
                '&:focus': {
                  backgroundColor: 'white',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            className={styles.submissionFormInput + ' h-12'}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 224,
                  width: 250,
                  // I am sorry about it
                  '::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '::-webkit-scrollbar-track': {
                    background: '#fcd559',
                  },
                  '::-webkit-scrollbar-thumb': {
                    background: '#000000',
                    borderRadius: '6px',
                  },
                  '::-webkit-scrollbar-thumb:hover': {
                    background: '#000000',
                  },
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {options.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </SubmissionInputWrapper>
  );
};

export const SubmissionFormSelect: React.FC<SubmissionFormSelectProps> =
  props => <SubmissionFormCommonSelect {...props} isMulti={false} />;

export const SubmissionFormMultiSelect: React.FC<SubmissionFormSelectProps> =
  props => <SubmissionFormCommonSelect {...props} isMulti={true} />;

export const SubmissionFormRadio: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> &
    InputProps & { options: { value: string; label: string }[] }
> = ({ name, label, options }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<ProjectSubmissionFormInterface>();

  return (
    <SubmissionInputWrapper
      name={name}
      label={label}
      errorMessage={
        errors[name as keyof ProjectSubmissionFormInterface]?.message
      }
    >
      <div className="flex gap-4">
        {options.map(({ value, label: optionLabel }) => (
          <label key={value} className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value={value}
              className={styles.radioInput}
            />
            {optionLabel}
          </label>
        ))}
      </div>
    </SubmissionInputWrapper>
  );
};

interface SubmissionFormRawFileInputProps extends InputProps {
  validFormats?: string[];
  validRes?: { height: number; width: number };
}

const SubmissionFormRawFileInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & SubmissionFormRawFileInputProps
> = ({
  name,
  validFormats = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
  validRes,
}) => {
  const { setValue } = useFormContext<ProjectSubmissionFormInterface>();
  const formFile = useFormContext().watch(name) as File | undefined;
  const formFileName = formFile?.name;

  const [fileName, setFileName] = useState<string | undefined>(formFileName);

  const setFile = (
    _value?: File | undefined,
    _fileName?: string | undefined,
  ) => {
    setValue(name, _value);
    setFileName(_fileName);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files![0];

    if (file.size > MAX_FILE_SIZE) {
      alert('File size exceeds 10MB');
      setFile();
    } else if (!validFormats.includes(file.type)) {
      alert('Invalid file type');
      setFile();
    } else if (validRes != null) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = function () {
        if (img.width > validRes.width || img.height > validRes.height) {
          alert('Image size should be 1680x450');
          setFile();
        } else {
          setFile(file, file.name);
        }
      };
    } else {
      setFile(file, file.name);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input id={name} type="file" onChange={handleFileChange} />
      <label htmlFor={name} className={`${styles.fileInputLabel} w-fit`}>
        <CloudUpload />
        <p>Upload</p>
      </label>
      <div className="w-full flex items-center gap-1">
        <p className="text-sm overflow-hidden overflow-ellipsis">
          {fileName ?? '\u00A0'}
        </p>
        {fileName != null && (
          <button
            type="button"
            onClick={() => setFile()}
            className="flex text-md"
          >
            <Close fontSize="inherit" />
          </button>
        )}
      </div>
    </div>
  );
};

const SubmissionFormFileInputHeader: React.FC<{
  label?: string;
  formats?: string;
}> = ({ label, formats }) => (
  <div className="flex flex-col">
    <label className="m-0 text-lg font-semibold">{label}</label>
    <p className="text-sm m-0">{formats}</p>
  </div>
);

export const SubmissionFormFileInput: React.FC<
  InputProps & SubmissionFormRawFileInputProps & { formatsInfo?: string }
> = ({ name, label, formatsInfo, validFormats, validRes }) => (
  <div className="flex flex-col gap-3 mb-4">
    <SubmissionFormFileInputHeader label={label} formats={formatsInfo} />
    <SubmissionFormRawFileInput
      name={name}
      validFormats={validFormats}
      validRes={validRes}
    />
  </div>
);

export const SubmissionFormTeamInput = () => {
  const { control } = useFormContext<ProjectSubmissionFormInterface>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'team',
  });

  return (
    <div className="flex flex-col gap-4 items-start mb-4">
      <SubmissionFormFileInputHeader
        label="Team / Artist Profile"
        formats=".jpg / .png/ .gif (size limit 10mb) 1680x450"
      />
      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-row gap-4 items-center w-full">
          <button type="button" onClick={() => remove(index)}>
            <Close />
          </button>
          <div className="w-64">
            <SubmissionFormInput
              name={`team.${index}.name` as const}
              label="Name"
            />
          </div>
          <div className="relative top-3">
            <SubmissionFormRawFileInput
              name={`team.${index}.image` as const}
              label="Image of Person"
              validFormats={['image/jpeg', 'image/png', 'image/gif']}
              validRes={{ height: 450, width: 1680 }}
            />
          </div>
        </div>
      ))}
      {fields.length < 5 && (
        <button
          type="button"
          onClick={() => append({ name: '', image: undefined })}
          className="flex items-center gap-2"
        >
          <AddCircleOutline />
          <p>Add another team / artist</p>
        </button>
      )}
    </div>
  );
};
